
import React, { useState } from 'react';
import { Camera, Clock, MessageSquare, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { PhotoGallery } from './PhotoGallery';
import { SunnyHoursForm } from './SunnyHoursForm';
import { CommentForm } from './CommentForm';
import { useVisitorFeedback } from './useVisitorFeedback';

interface VisitorFeedbackSectionProps {
  terraceId: string;
  terraceName: string;
}

export const VisitorFeedbackSection: React.FC<VisitorFeedbackSectionProps> = ({
  terraceId,
  terraceName
}) => {
  const [activeTab, setActiveTab] = useState('photos');
  const { 
    photos, 
    sunnyHours, 
    comments,
    submitPhoto,
    submitFile,
    submitSunnyHours,
    submitComment,
    loading 
  } = useVisitorFeedback(terraceId);

  return (
    <div className="w-full">
      {/* Header Section */}
      <div className="pb-6 border-b border-sunset-medium/10">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 rounded-lg bg-peach-orange/10">
            <Send className="h-5 w-5 text-peach-orange" />
          </div>
          <h2 className="text-xl font-semibold text-sunset-dark">
            Visitor Check-ins
          </h2>
        </div>
        <p className="text-sunset-medium ml-11">
          Share your experience at {terraceName}
        </p>
      </div>

      {/* Tabs Navigation */}
      <div className="pt-4">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="photos" className="flex items-center gap-2">
              <Camera className="h-4 w-4" />
              <span className="hidden sm:inline">Photos</span>
              <span className="sm:hidden">Photos</span>
            </TabsTrigger>
            <TabsTrigger value="sunny-hours" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              <span className="hidden sm:inline">Sunny Hours</span>
              <span className="sm:hidden">Hours</span>
            </TabsTrigger>
            <TabsTrigger value="comments" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Comments</span>
              <span className="sm:hidden">Comments</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="photos">
            <PhotoGallery 
              photos={photos} 
              onSubmitPhoto={submitPhoto}
              onSubmitFile={submitFile}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="sunny-hours">
            <SunnyHoursForm 
              existingHours={sunnyHours}
              onSubmitHours={submitSunnyHours}
              loading={loading}
            />
          </TabsContent>

          <TabsContent value="comments">
            <CommentForm 
              comments={comments}
              onSubmitComment={submitComment}
              loading={loading}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};
